/**
 * Sign-in (ADR §6.1). Two methods (operator decision 17 Oct 2026):
 *  - Google Workspace: the button starts GET /api/v1/auth/google/start, Google sends the user back through the
 *    API's callback, and failures come back here as `?error=<SignInError>` — each printed with the contract's own
 *    message (guide §9: say what went wrong and how to fix it).
 *  - Email + password: POST /auth/password/sign-in. One message for every wrong answer (it never says whether the
 *    email exists); 429 says the email is locked or rate-limited. A temporary password signs in with
 *    must_change_password set, and the app then shows only "Choose a new password" (screens/ChangePassword).
 *
 * Local and test runs also get "Sign in with an email" against POST /auth/dev/sign-in. The API registers
 * that route only outside production with AUTH_DEV_LOGIN=1, and applies the same active-user check.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SIGN_IN_ERROR_MESSAGE, SafeRedirect, SignInError, routes } from '@amplispot/contracts';
import { ApiFailure, call } from '../api';
import { ME_KEY } from '../lib/session';
import { passwordSignInError } from '../lib/password';
import { Icon } from '../ui/Icon';

const DEV_SIGN_IN = import.meta.env.DEV || import.meta.env.VITE_AUTH_DEV_LOGIN === '1';

export function SignIn() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const redirect = SafeRedirect.safeParse(params.get('redirect') ?? undefined);
  const target = redirect.success ? redirect.data : undefined;
  const err = SignInError.safeParse(params.get('error') ?? undefined);

  // Already signed in (e.g. the back button): go straight in.
  const me = useQuery({ queryKey: ME_KEY, queryFn: () => call('me.get'), retry: false });

  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [devError, setDevError] = useState<string | null>(null);
  const [pwEmail, setPwEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Sign in · Amplispot Ops';
    return () => {
      document.title = 'Amplispot Ops';
    };
  }, []);

  if (me.data) return <Navigate to={target ?? me.data.home_route} replace />;

  const googleHref = routes['auth.googleStart'].path + (target ? `?redirect=${encodeURIComponent(target)}` : '');

  const enter = async () => {
    await qc.invalidateQueries({ queryKey: ME_KEY });
    const fresh = await qc.fetchQuery({ queryKey: ME_KEY, queryFn: () => call('me.get') });
    // A temporary password: the app shows only "Choose a new password" until it is changed, then goes on.
    nav(fresh.must_change_password ? '/' : (target ?? fresh.home_route), { replace: true });
  };

  const passwordSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setPwBusy(true);
    setPwError(null);
    try {
      await call('auth.passwordSignIn', { body: { email: pwEmail, password } });
      await enter();
    } catch (x) {
      setPwError(passwordSignInError(x));
      setPassword('');
    } finally {
      setPwBusy(false);
    }
  };

  const devSignIn = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setDevError(null);
    try {
      await call('auth.devSignIn', { body: { email } });
      await enter();
    } catch (x) {
      if (x instanceof ApiFailure && x.status === 404) setDevError('Email sign-in is switched off on this server. Use Sign in with Google.');
      else if (x instanceof ApiFailure && x.code === 'not_provisioned') setDevError(SIGN_IN_ERROR_MESSAGE.not_provisioned);
      else if (x instanceof ApiFailure && x.code === 'validation_failed') setDevError('Enter a full email address, for example name@amplispot.com');
      else setDevError(x instanceof Error ? x.message : 'Sign-in failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <div className="signin-wrap">
        <SignInBrand />
        <main className="signin-card">
          <div>
            <h1>Sign in</h1>
            <p className="lead">Use your amplispot.com Google account, or your work email and password. You see the clients and work your role gives you.</p>
          </div>
          {err.success ? (
            <div className="note bad" role="alert">
              {SIGN_IN_ERROR_MESSAGE[err.data]}
            </div>
          ) : null}
          <a className="btn pri gbtn" href={googleHref} style={{ textDecoration: 'none' }}>
            <Icon name="google" />
            Sign in with Google
          </a>
          <form onSubmit={passwordSignIn} style={{ display: 'grid', gap: 12 }} aria-label="Sign in with email and password">
            <div className="or">or with your password</div>
            {pwError ? (
              <div className="note bad" role="alert">
                {pwError}
              </div>
            ) : null}
            <label className="fld" style={{ marginBottom: 0 }}>
              <span>Work email</span>
              <input
                className="in"
                type="email"
                name="email"
                required
                autoComplete="username"
                placeholder="name@amplispot.com"
                value={pwEmail}
                onChange={(e) => setPwEmail(e.target.value)}
              />
            </label>
            <label className="fld" style={{ marginBottom: 0 }}>
              <span>Password</span>
              <input
                className="in"
                type="password"
                name="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={pwError ? true : undefined}
              />
            </label>
            <button className="btn" type="submit" disabled={pwBusy || !pwEmail || !password} style={{ justifyContent: 'center' }}>
              {pwBusy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
          {DEV_SIGN_IN ? (
            <form onSubmit={devSignIn} style={{ display: 'grid', gap: 12 }}>
              <div className="or">Local and test runs</div>
              <label className="fld" style={{ marginBottom: 0 }}>
                <span>Work email</span>
                <input
                  className="in"
                  type="email"
                  required
                  autoComplete="email"
                  placeholder="name@amplispot.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-invalid={devError ? true : undefined}
                />
                {devError ? <small className="ferr">{devError}</small> : null}
              </label>
              <button className="btn" type="submit" disabled={busy || !email} style={{ justifyContent: 'center' }}>
                {busy ? 'Signing in…' : 'Sign in with an email'}
              </button>
            </form>
          ) : null}
          <p className="fine">No account yet, or forgot your password? Ask a manager: they add people and reset passwords in Settings.</p>
        </main>
      </div>
    </div>
  );
}

/** The Amplispot Ops mark above the card (sign-in and "Choose a new password"). */
export function SignInBrand() {
  return (
    <div className="signin-brand">
      <div className="glyph" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </div>
      <div>
        <div className="lg1" style={{ fontWeight: 800, fontSize: 20, lineHeight: 1.05 }}>
          Amplispot
        </div>
        <div className="lg2" style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '.2em', color: '#B9B2F0', textTransform: 'uppercase' }}>
          Ops
        </div>
      </div>
    </div>
  );
}
